<template>

  <section class="py-12">
    <div class="grid grid-cols-12 gap-4 lg:gap-8 container mx-auto">
      <!-- Sidebar -->
      <aside class="col-span-12 lg:col-span-4 xl:col-span-3 self-start px-4 lg:px-0">
        <div>
          <MmMaterialsFilters
            :model-value="filtersModel"
            :categories="filterCategories"
            @update:modelValue="updateFilters"
            @filters-change="onFiltersChange"
            @locate-me="onLocateMe"
          />
        </div>
      </aside>

      <!-- Results list -->
      <main class="col-span-12 lg:col-span-8 xl:col-span-9 px-4 lg:px-0">
        <!-- Top bar: sort & view switcher -->
        
        <div class="mb-6 flex flex-wrap items-center justify-between gap-4 lg:px-0">
          <div class="hidden lg:block">
            <!-- Placeholder to keep the 'justify-between' working and align the right side correctly -->
          </div>
          <div class="flex items-center gap-2 lg:order-2">
            <button
              @click="viewMode = 'grid'"
              class="flex h-10 w-10 items-center justify-center border border-black transition-colors focus:outline-none focus:ring-0"
              :class="viewMode === 'grid' ? 'bg-black text-white' : 'bg-white text-black'"
              :aria-label="t('aria.gridView')"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>
            </button>
            <button
              @click="viewMode = 'list'"
              class="flex h-10 w-10 items-center justify-center border border-black transition-colors focus:outline-none focus:ring-0"
              :class="viewMode === 'list' ? 'bg-black text-white' : 'bg-white text-black'"
              :aria-label="t('aria.listView')"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>
            </button>

            <MmSortDropdown
              :options="sortOptions"
              :current-key="sort.key"
              :current-label="currentSortLabel"
              @change="selectSort"
            />
          </div>
        </div>

        <div :class="[
          'grid gap-4 sm:gap-6 lg:px-0',
          viewMode === 'grid' ? 'grid-cols-2 xs:grid-cols-3 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'
        ]">
          <template v-for="(m, idx) in decoratedItems" :key="m.__key">
            <div v-if="m.__type === 'promo' && !isLoggedIn" :class="viewMode === 'grid' ? 'col-span-full m-0' : 'col-span-1 m-0'">
              <MmProductPromo />
            </div>

            <MmProductCard
              v-else-if="m.__type==='material'"
              :item="m.data"
              :placeholder-img="placeholderImg"
              :detail-href="detailHref(m.data)"
              :show-status="true"
              :view-mode="viewMode"
            />
          </template>
        </div>

        <!-- Pagination -->
        <MmPagination :page="currentPage" :pages="pages" @page-change="onPageChange" />
      </main>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, reactive, watch, onMounted, onBeforeUnmount } from 'vue'
import MmMaterialsFilters from './MmMaterialsFilters.vue'
import MmSortDropdown from './MmSortDropdown.vue'
import MmProductPromo from './MmProductPromo.vue'
import MmProductCard from './MmProductCard.vue'
import MmPagination from './MmPagination.vue'
import WpApiClient from '../lib/wp-api.js'
import { t as i18nT } from '../i18n'

const t = (key) => i18nT(key)

const viewMode = ref('grid')
const windowWidth = ref(window.innerWidth)

const updateWidth = () => {
  windowWidth.value = window.innerWidth
}

onMounted(() => {
  window.addEventListener('resize', updateWidth)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateWidth)
})

// Runtime props only
const props = defineProps({
  items: { type: Array, default: () => [] },
  categories: { type: Array, default: () => [] },
  // Default to true (assume logged-in) to avoid showing promo unintentionally if status isn't provided
  isLoggedIn: { type: Boolean, default: true },
  page: { type: Number, default: 1 },
  totalPages: { type: Number, default: 5 },
  initialFilters: { type: Object, default: () => ({ query: '', locationText: '', radius: 25, selectedCats: [] }) },
  buildDetailHref: { type: Function, default: null },
  // New: auto-fetch materials/categories from WordPress
  autoFetch: { type: Boolean, default: undefined }, // undefined means: auto true when no items provided
  materialsEndpoint: { type: String, default: '/wp-json/wp/v2/materiaal' },
  categoriesEndpoint: { type: String, default: '/wp-json/wp/v2/materiaal_categorie' },
  perPage: { type: Number, default: 24 },
})

const api = new WpApiClient({ cacheBust: true }) // uses window.MateriaalMaatjes.apiBase when set

const emit = defineEmits(['page-change','filters-change','sort-change'])

// Sorting dropdown
const openSort = ref(false)
const sortOptions = [
  { key: 'newest', label: t('materials.sortNewOld') },
  { key: 'oldest', label: t('materials.sortOldNew') },
  { key: 'price-asc', label: t('materials.sortPriceAsc') },
  { key: 'price-desc', label: t('materials.sortPriceDesc') },
]
const sort = ref({ key: 'newest', label: t('materials.sortNewOld') })
const currentSortLabel = computed(()=> sort.value.label)
function selectSort(o){ sort.value = o; openSort.value = false; emit('sort-change', o); scheduleFetch() }

// Filters model handled by child component
const filtersModel = reactive({ ...props.initialFilters })
function updateFilters(payload){
  // Merge into existing reactive object to keep references stable
  if (payload && typeof payload === 'object') {
    const coerced = { ...payload }
    // Enforce single category selection defensively
    if (Array.isArray(coerced.selectedCats)) {
      const flat = coerced.selectedCats.map(n => Number(n)).filter(n => Number.isFinite(n) && n > 0)
      coerced.selectedCats = flat.length > 1 ? [flat[0]] : flat
    }
    Object.assign(filtersModel, coerced)
  }
}
function onFiltersChange(payload){ emit('filters-change', payload); scheduleFetch() }

// Optional: handle "locate me" button from filters component
async function onLocateMe(){
  if (!navigator?.geolocation) {
    console.warn('[MateriaalMaatjes] Geolocation not supported')
    return
  }

  loading.value = true
  try {
    const geoOptions = { maximumAge: 60000, timeout: 10000, enableHighAccuracy: true }
    
    // Wrap geolocation in a Promise for better async handling
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, geoOptions)
    })

    const { latitude, longitude } = position.coords
    let locationName = ''
    
    // Try reverse geocoding via Nominatim (OSM) to get a readable city name
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&zoom=14`, {
        headers: { 
          'Accept-Language': 'nl',
          'User-Agent': 'MateriaalMaatjes/1.0 (https://materiaal-maatjes.nl)'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        const addr = data.address || {}
        
        // Priority order for Dutch location names
        const city = addr.city || addr.town || addr.village || addr.municipality || addr.city_district || addr.suburb || addr.neighborhood || addr.locality || addr.county
        
        console.log('[MateriaalMaatjes] Nominatim address:', addr)
        console.log('[MateriaalMaatjes] Detected city:', city)

        if (city) {
          locationName = city
        }
      } else {
        console.warn('[MateriaalMaatjes] Nominatim API error:', response.status)
      }
    } catch (err) {
      console.warn('[MateriaalMaatjes] Reverse geocoding failed:', err)
    }

    if (locationName) {
      // Clear existing location data first to avoid keeping old coordinates
      filtersModel.locationData = null
      
      // Update property directly
      filtersModel.locationText = locationName
      // Also trigger a re-assignment to ensure Vue's deep watcher on the object triggers
      Object.assign(filtersModel, { ...filtersModel })
      
      // Update URL proactively
      updateUrlFromState()
      
      // Explicitly trigger fetch to be sure
      scheduleFetch()
    } else {
      console.warn('[MateriaalMaatjes] Could not resolve coordinates to a city name:', latitude, longitude)
      // Optional: alert user if we can't find a name
    }
  } catch (err) {
    console.warn('[MateriaalMaatjes] Geolocation failed or denied:', err)
    // Handle specific error codes if needed
  } finally {
    loading.value = false
  }
}

// Auto-fetch enablement logic: true if explicit true, or when no items provided
const autoFetchEnabled = computed(() => (props.autoFetch === undefined ? (props.items.length === 0) : !!props.autoFetch))

// Remote data state
const remoteItems = ref([])
const remoteTotalPages = ref(props.totalPages || 1)
const loading = ref(false)
const error = ref(null)

// Categories: use provided if any, else fetch
const categoriesLocal = ref([])
const filterCategories = computed(() => (props.categories && props.categories.length ? props.categories : categoriesLocal.value))

// Helpers to resolve a category image client-side when mm_primary_category.image_url is empty
const imageFieldCandidates = ['image', 'thumbnail', 'term_image', 'icon']
function getImageFromAcf(acf){
  if (!acf || typeof acf !== 'object') return ''
  for (const key of imageFieldCandidates) {
    const img = acf[key]
    if (!img) continue
    if (typeof img === 'string') return img
    if (img?.sizes?.medium) return img.sizes.medium
    if (img?.url) return img.url
  }
  return ''
}
function resolveCategoryImageById(id){
  try {
    const list = filterCategories.value || []
    const c = list.find(x => Number(x?.id) === Number(id))
    if (!c) return ''
    return c.image_url || c.imageUrl || getImageFromAcf(c.acf) || c.image || c.thumbnail || ''
  } catch (_) { return '' }
}

// Source items depending on autoFetch
const sourceItems = computed(() => (autoFetchEnabled.value ? remoteItems.value : props.items))

// De database sorteert de items al op claim-status (beschikbaar eerst).
// We gebruiken de items direct zoals ze binnenkomen om overhead te voorkomen.
const listItems = computed(() => sourceItems.value)

const decoratedItems = computed(() => {
  const items = listItems.value.map(m => ({ __type: 'material', __key: `m-${m.id}`, data: m }))
  
  if (!isLoggedIn.value) {
    let targetIdx = 2 // List mode default (3rd position)
    
    if (viewMode.value === 'grid') {
      // In grid view:
      // Desktop/tablet (sm and up) -> 4th item (idx 3)
      // Mobile (below sm/640px) -> 5th item (idx 4)
      targetIdx = windowWidth.value >= 640 ? 3 : 4
    }
    
    // Alleen invoegen als we genoeg items hebben, anders aan het einde
    const actualIdx = Math.min(items.length, targetIdx)
    items.splice(actualIdx, 0, { __type: 'promo', __key: 'promo-block' })
  }
  
  return items
})

// Pagination
const pageRef = computed(() => props.page || 1)
const pages = computed(() => {
  const total = autoFetchEnabled.value ? (remoteTotalPages.value || 1) : (props.totalPages || 1)
  return Array.from({ length: total }, (_,i) => i+1)
})

function itemTitle(m){ return m.title || m.name || '' }
function priceText(p){ return (p != null && !Number.isNaN(Number(p))) ? Number(p).toFixed(0) : '—' }
const placeholderImg = 'data:image/svg+xml;utf8,' + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300"><rect width="400" height="300" fill="#f3f4f6"/></svg>`) 

function detailHref(m){
  if (typeof props.buildDetailHref === 'function') return props.buildDetailHref(m)
  return (m?.slug && m?.id) ? `/materiaal/${m.slug}-${m.id}` : (m?.slug ? `/materiaal/${m.slug}` : `#materiaal-${m?.id}`)
}

const isLoggedIn = computed(() => !!props.isLoggedIn)
const page = computed(() => pageRef.value)
// Local page state to ensure active pagination button reflects immediately
const currentPage = ref(Number(pageRef.value) || 1)

// --- Fetch logic ---
function buildMaterialsUrl(){
  const url = new URL((props.materialsEndpoint || '/wp-json/wp/v2/materiaal'), window.location.origin)
  const params = url.searchParams
  params.set('page', String(page.value))
  params.set('per_page', String(props.perPage || 24))
  params.set('_embed', '1')
  // search
  if (filtersModel.query) params.set('search', String(filtersModel.query))
  // type
  if (filtersModel.type) params.set('type', String(filtersModel.type))
  // categories filter (custom taxonomy param name)
  if (Array.isArray(filtersModel.selectedCats) && filtersModel.selectedCats.length){
    params.set('materiaal_categorie', filtersModel.selectedCats.join(','))
  }
  // sort
  switch (sort.value.key) {
    case 'price-asc':
      params.set('orderby','meta_value_num'); params.set('meta_key','mm_waarde_eur'); params.set('order','asc'); break
    case 'price-desc':
      params.set('orderby','meta_value_num'); params.set('meta_key','mm_waarde_eur'); params.set('order','desc'); break
    case 'oldest':
      params.set('orderby','date'); params.set('order','asc'); break
    case 'newest':
    default:
      params.set('orderby','date'); params.set('order','desc'); break
  }
  return url.toString()
}

function stripTags(html){
  const tmp = document.createElement('div'); tmp.innerHTML = String(html||''); return (tmp.textContent||tmp.innerText||'').trim()
}

function mapMaterialFromWP(post){
  // Featured image via _embedded if present
  const media = post?._embedded?.['wp:featuredmedia']?.[0]
  const image = media?.media_details?.sizes?.large?.source_url || media?.source_url || null
  const meta = post?.meta || {}
  // Primary category from custom REST fields
  const cat = post?.eigenschappen?.categorie || post?.mm_primary_category || null
  const resolvedImg = cat?.id ? resolveCategoryImageById(cat.id) : ''
  const category = cat ? ({ id: cat.id, name: cat.name, slug: cat.slug, imageUrl: cat.image_url || cat.imageUrl || resolvedImg || '' }) : null
  // Provider (aanbieder) from embedded author
  const author = post?._embedded?.author?.[0] || null
  const providerName = author?.mm_org_name || author?.name || post?.author_name || author?.slug || null
  const providerWebsite = author?.url || author?.website || post?.author_website || null
  const providerLogo = author?.mm_company_logo_url || post?.author_company_logo_url || null
  const provider = (providerName || providerWebsite || providerLogo) ? {
    name: providerName,
    website: providerWebsite,
    avatarUrl: (author?.avatar_urls && (author.avatar_urls['96'] || author.avatar_urls['48'] || author.avatar_urls['24'])) || null,
    companyLogoUrl: providerLogo,
  } : null
  // Claim info from REST extension
  const claim = post?.mm_claim || null
  const claimed = !!(claim && Number(claim.claimed_by_user || 0) !== 0 && String(claim.status || '').toLowerCase() !== 'cancelled')
  // Locatie uit verschillende mogelijke bronnen halen, zodat het ook werkt
  // wanneer meta niet (goed) via REST wordt blootgesteld.
  const locationText = (meta?.mm_locatie
    ?? post?.mm_locatie
    ?? post?.location
    ?? null)
  const locationUrl = locationText ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationText)}` : null
  return {
    id: post?.id,
    title: post?.title?.rendered || '',
    excerpt: stripTags(post?.excerpt?.rendered || ''),
    description: undefined,
    image,
    price: post?.eigenschappen?.waarde_eur ?? meta?.mm_waarde_eur ?? null,
    location: locationText || null,
    locationText: locationText || null,
    locationUrl,
    views: post?.mm_views ?? meta?.mm_views ?? post?.meta?.mm_views ?? 0,
    isTooLate: claimed,
    slug: post?.slug,
    category,
    provider,
    mm_author_role: post?.mm_author_role ?? post?.author_role ?? author?.mm_author_role ?? author?.author_role ?? null,
    mm_type: post?.mm_type ?? meta?.mm_type ?? post?.type ?? null,
    // minimal flag so cards can show a claimed label in frontend
    claimed,
  }
}

async function fetchMaterials(){
  if (!autoFetchEnabled.value) return
  loading.value = true; error.value = null
  try {
    const { items, totalPages } = await api.getMaterials({
      endpoint: props.materialsEndpoint.replace('/wp-json/wp/v2','') || '/materiaal',
      page: page.value,
      perPage: props.perPage || 24,
      sort: sort.value.key,
      search: filtersModel.query || '',
      categories: Array.isArray(filtersModel.selectedCats) ? filtersModel.selectedCats : [],
      location: filtersModel.locationText || '',
      radius: Number(filtersModel.radius || 0) || undefined,
      type: filtersModel.type || '',
      location_lat: filtersModel.locationData?.lat ?? undefined,
      location_lon: filtersModel.locationData?.lon ?? undefined,
    })
    remoteItems.value = Array.isArray(items) ? items.map(mapMaterialFromWP) : []
    remoteTotalPages.value = totalPages
  } catch (e) {
    console.warn('[MateriaalMaatjes] fetch materials failed:', e)
    error.value = e?.status ? `HTTP ${e.status}` : (e?.message || String(e))
  } finally {
    loading.value = false
  }
}

async function fetchCategories(){
  if (props.categories && props.categories.length) return
  try {
    const taxonomy = (props.categoriesEndpoint || '/wp-json/wp/v2/materiaal_categorie').split('/').pop() || 'materiaal_categorie'
    // Request common image fields too so the filter can render thumbnails like MmCategories.vue
    const data = await api.getCategories({ taxonomy, perPage: 100, fields: 'id,name,slug,count,acf,image_url,image,thumbnail' })
    categoriesLocal.value = Array.isArray(data)
      ? data.map(t => ({
          id: t.id,
          name: t.name,
          slug: t.slug,
          count: t.count,
          // pass through possible image sources so MmMaterialsFilters can pick them up via its fallbacks
          image_url: t.image_url || '',
          image: t.image || '',
          thumbnail: t.thumbnail || '',
          acf: t.acf || null,
        }))
      : []
  } catch (e) {
    // non-blocking
  }
}

let fetchTimer = null
function scheduleFetch(){
  if (!autoFetchEnabled.value) return
  if (fetchTimer) clearTimeout(fetchTimer)
  fetchTimer = setTimeout(() => { fetchMaterials() }, 250)
}

watch(() => props.page, (v) => { currentPage.value = Number(v || 1); scheduleFetch() })
watch(() => ({...filtersModel}), () => scheduleFetch(), { deep: true })
watch(() => sort.value.key, () => scheduleFetch())

// Handle page change from pagination component
async function onPageChange(newPage){
  const target = Number(newPage) || 1
  // Update local page immediately so active state in pagination reflects correctly
  currentPage.value = target
  emit('page-change', target)
  if (!autoFetchEnabled.value) return
  // Fetch immediately for the requested page to avoid relying solely on a parent to update props
  loading.value = true; error.value = null
  try {
    const { items, totalPages } = await api.getMaterials({
      endpoint: props.materialsEndpoint.replace('/wp-json/wp/v2','') || '/materiaal',
      page: target,
      perPage: props.perPage || 24,
      sort: sort.value.key,
      search: filtersModel.query || '',
      categories: Array.isArray(filtersModel.selectedCats) ? filtersModel.selectedCats : [],
      location: filtersModel.locationText || '',
      radius: Number(filtersModel.radius || 0) || undefined,
      type: filtersModel.type || '',
      location_lat: filtersModel.locationData?.lat ?? undefined,
      location_lon: filtersModel.locationData?.lon ?? undefined,
    })
    remoteItems.value = Array.isArray(items) ? items.map(mapMaterialFromWP) : []
    remoteTotalPages.value = totalPages
  } catch (e) {
    console.warn('[MateriaalMaatjes] fetch materials (page change) failed:', e)
    error.value = e?.status ? `HTTP ${e.status}` : (e?.message || String(e))
  } finally {
    loading.value = false
  }
  // Update URL page parameter proactively
  try {
    const url = buildUrlFromState()
    const p = url.searchParams
    if (target > 1) p.set('page', String(target))
    else p.delete('page')
    window.history.replaceState({}, '', url.toString())
  } catch (_) {}
}

// --- URL sync helpers ---
let isApplyingUrl = false
let pendingCategorySlug = ''

function parseCats(val){
  if (!val) return []
  return String(val)
    .split(',')
    .map(s => Number(s.trim()))
    .filter(n => Number.isFinite(n) && n > 0)
}

function getMaterialsBasePath(){
  // Prefer '/materialen' as canonical base. If current path includes it, use that.
  try {
    const path = window.location.pathname.replace(/\/+$/, '') || '/'
    const parts = path.split('/').filter(Boolean)
    const idx = parts.indexOf('materialen')
    if (idx !== -1) return '/' + parts.slice(0, idx + 1).join('/')
  } catch (_) {}
  return '/materialen'
}

function findCategoryById(id){
  const list = filterCategories.value || []
  return list.find(c => Number(c?.id) === Number(id)) || null
}
function findCategoryBySlug(slug){
  if (!slug) return null
  const list = filterCategories.value || []
  return list.find(c => String(c?.slug) === String(slug)) || null
}

function buildUrlFromState(){
  const url = new URL(window.location.href)
  const p = url.searchParams
  // query
  if (filtersModel.query) p.set('q', String(filtersModel.query))
  else p.delete('q')
  // location (omit when empty)
  if (filtersModel.locationText) p.set('location', String(filtersModel.locationText))
  else p.delete('location')
  
  // lat/lon for precise radius filtering on refresh
  if (filtersModel.locationData?.lat) p.set('location_lat', String(filtersModel.locationData.lat))
  else p.delete('location_lat')
  if (filtersModel.locationData?.lon) p.set('location_lon', String(filtersModel.locationData.lon))
  else p.delete('location_lon')
  
  // radius
  if (filtersModel.radius && filtersModel.radius !== 25) p.set('radius', String(filtersModel.radius))
  else if (filtersModel.locationText) p.set('radius', String(filtersModel.radius || 25))
  else p.delete('radius')

  // type filter
  if (filtersModel.type) p.set('type', String(filtersModel.type))
  else p.delete('type')
  // sort (omit default newest)
  if (sort.value?.key && sort.value.key !== 'newest') p.set('sort', sort.value.key)
  else p.delete('sort')
  // page (omit 1)
  const pg = Number(page.value || 1)
  if (pg > 1) p.set('page', String(pg))
  else p.delete('page')

  // Path: /materialen or /materialen/{slug}
  const base = getMaterialsBasePath()
  let newPath = base
  const hasCat = Array.isArray(filtersModel.selectedCats) && filtersModel.selectedCats.length > 0
  if (hasCat) {
    const id = Number(filtersModel.selectedCats[0])
    const cat = findCategoryById(id)
    const slug = cat?.slug || ''
    if (slug) newPath = `${base}/${slug}`
  }
  try {
    url.pathname = newPath
  } catch (_) {
    // Fallback: rebuild absolute URL
    return new URL(`${newPath}${p.toString() ? ('?' + p.toString()) : ''}`, window.location.origin)
  }
  return url
}

function updateUrlFromState(){
  try {
    if (isApplyingUrl) return
    const url = buildUrlFromState()
    window.history.replaceState({}, '', url.toString())
  } catch (_) {}
}

function applyStateFromUrl(){
  try {
    isApplyingUrl = true
    const params = new URLSearchParams(window.location.search)
    const q = params.get('q') || ''
    const loc = params.get('location') || ''
    const sortKey = params.get('sort') || 'newest'
    const pg = Number(params.get('page') || '1')

    // apply filters
    filtersModel.query = q
    filtersModel.locationText = loc
    const typeParam = params.get('type') || ''
    filtersModel.type = typeParam

    // restore lat/lon/radius
    const lat = params.get('location_lat')
    const lon = params.get('location_lon')
    const rad = params.get('radius')
    if (lat && lon) {
      filtersModel.locationData = {
        lat: parseFloat(lat),
        lon: parseFloat(lon),
        display_name: loc,
        place_name: loc.split(',')[0],
        full_name: loc // Fallback for full name
      }
    }
    if (rad) {
      filtersModel.radius = parseInt(rad, 10)
    }

    // Determine category from pretty path or legacy ?cat query
    const path = window.location.pathname.replace(/\/+$/, '')
    const base = getMaterialsBasePath()
    let slug = ''
    if (path.startsWith(base)) {
      const rest = path.slice(base.length).replace(/^\//, '')
      slug = rest.split('/').filter(Boolean)[0] || ''
    }
    const legacyCatIds = parseCats(params.get('cat'))

    if (slug) {
      const cat = findCategoryBySlug(slug)
      if (cat) {
        filtersModel.selectedCats = [Number(cat.id)]
      } else {
        // categories might not be loaded yet; keep for later
        pendingCategorySlug = slug
        filtersModel.selectedCats = []
      }
    } else if (legacyCatIds.length) {
      // Backward compatibility: accept ?cat=id and normalize path asap
      filtersModel.selectedCats = [legacyCatIds[0]]
      // Will update URL to slug once categories are known
    } else {
      filtersModel.selectedCats = []
    }

    // apply sort if valid
    const opt = sortOptions.find(o => o.key === sortKey) || sortOptions[0]
    sort.value = opt
    // apply page by notifying parent if different
    if (pg !== page.value){ emit('page-change', pg) }
  } finally {
    isApplyingUrl = false
  }
}

// update URL when state changes
watch(() => ({ 
  q: filtersModel.query, 
  loc: filtersModel.locationText, 
  lat: filtersModel.locationData?.lat,
  lon: filtersModel.locationData?.lon,
  radius: filtersModel.radius,
  cats: [...(filtersModel.selectedCats||[])], 
  type: filtersModel.type 
}), () => { updateUrlFromState() }, { deep: true })
watch(() => sort.value.key, () => { updateUrlFromState() })
watch(() => page.value, () => { updateUrlFromState() })

// Handle browser navigation
function onPopState(){ applyStateFromUrl(); scheduleFetch() }

onMounted(() => {
  // initialize from URL then fetch
  applyStateFromUrl()

  // Als de gebruiker ingelogd is en er geen locatie in de URL staat,
  // stel dan de locatiefilter in op de stad/postcode van de gebruiker.
  // De gebruiker kan dit altijd zelf overschrijven via de filter.
  if (props.isLoggedIn && !filtersModel.locationText) {
    try {
      const user = (window.materiaalAjax && window.materiaalAjax.user) || null
      const userLocation = (user && (user.city || user.postcode)) || ''
      if (userLocation) {
        filtersModel.locationText = userLocation
      }
    } catch (_) {}
  }

  if (autoFetchEnabled.value) {
    fetchCategories().then(() => {
      if (pendingCategorySlug) {
        const cat = findCategoryBySlug(pendingCategorySlug)
        if (cat) {
          filtersModel.selectedCats = [Number(cat.id)]
          pendingCategorySlug = ''
          updateUrlFromState()
        }
      }
      fetchMaterials()
    })
  }
  window.addEventListener('popstate', onPopState)
})

onBeforeUnmount(() => {
  window.removeEventListener('popstate', onPopState)
})
</script>

<style scoped>
</style>
